import { useEffect, useState } from 'preact/hooks';
import type { EpisodeDto, PlaylistSummaryDto } from '../../shared/types';
import { api, ApiError } from '../api';

interface Props {
  episode: EpisodeDto;
  onClose: () => void;
}

export function AddToPlaylistModal({ episode, onClose }: Props) {
  const [playlists, setPlaylists] = useState<PlaylistSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addedIds, setAddedIds] = useState<Set<number>>(new Set());
  const [creating, setCreating] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [creatingBusy, setCreatingBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .playlists()
      .then((res) => {
        if (!cancelled) setPlaylists(res.playlists);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load playlists.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAddToPlaylist = async (playlistId: number) => {
    try {
      await api.addToPlaylist(playlistId, episode.id);
      setAddedIds((prev) => new Set([...prev, playlistId]));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to add episode to playlist.');
    }
  };

  const handleCreateAndAdd = async (e: Event) => {
    e.preventDefault();
    const name = newPlaylistName.trim();
    if (!name) return;
    setCreatingBusy(true);
    setError(null);
    try {
      const res = await api.createPlaylist(name);
      await api.addToPlaylist(res.playlist.id, episode.id);
      setPlaylists((prev) => [
        ...prev,
        {
          id: res.playlist.id,
          name: res.playlist.name,
          createdAt: res.playlist.createdAt,
          itemCount: 1,
        },
      ]);
      setAddedIds((prev) => new Set([...prev, res.playlist.id]));
      setNewPlaylistName('');
      setCreating(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create playlist.');
    } finally {
      setCreatingBusy(false);
    }
  };

  return (
    <div
      class="modal-backdrop"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        class="modal-dialog panel"
        style={{
          maxWidth: '450px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-lg)',
          position: 'relative',
        }}
      >
        <div class="stack stack--row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>Add to Playlist</h2>
          <button type="button" class="icon-button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <p class="muted" style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>
          {episode.title}
        </p>

        {error && <p class="error" style={{ marginBottom: '1rem' }}>{error}</p>}

        {loading && <p class="muted">Loading playlists…</p>}

        {!loading && (
          <>
            {playlists.length === 0 && !creating && (
              <p class="muted" style={{ marginBottom: '1rem' }}>
                You don't have any playlists yet.
              </p>
            )}

            {playlists.length > 0 && (
              <div class="stack" style={{ gap: '0.5rem', marginBottom: '1rem' }}>
                {playlists.map((pl) => {
                  const isAdded = addedIds.has(pl.id);
                  return (
                    <div
                      key={pl.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.5rem 0.75rem',
                        background: 'var(--surface-2)',
                        borderRadius: 'var(--radius)',
                      }}
                    >
                      <div>
                        <strong>{pl.name}</strong>
                        <div class="muted" style={{ fontSize: '0.8rem' }}>
                          {pl.itemCount + (isAdded ? 1 : 0)} episodes
                        </div>
                      </div>
                      <button
                        type="button"
                        class={`button button--small ${isAdded ? 'button--ghost' : 'button--primary'}`}
                        disabled={isAdded}
                        onClick={() => handleAddToPlaylist(pl.id)}
                      >
                        {isAdded ? '✓ Added' : '+ Add'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {creating ? (
              <form onSubmit={handleCreateAndAdd} style={{ marginTop: '1rem' }}>
                <div class="stack stack--row" style={{ gap: '0.5rem' }}>
                  <input
                    type="text"
                    class="input"
                    placeholder="New playlist name"
                    value={newPlaylistName}
                    onInput={(e) => setNewPlaylistName((e.target as HTMLInputElement).value)}
                    maxLength={120}
                    required
                    autoFocus
                  />
                  <button type="submit" class="button button--primary" disabled={creatingBusy}>
                    {creatingBusy ? 'Creating…' : 'Create & Add'}
                  </button>
                  <button
                    type="button"
                    class="button button--ghost"
                    disabled={creatingBusy}
                    onClick={() => setCreating(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <button
                type="button"
                class="button button--ghost"
                style={{ width: '100%', marginTop: '0.5rem' }}
                onClick={() => setCreating(true)}
              >
                + New Playlist
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
